import { useEffect, useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { type Contact, listContacts } from "../api/crm.ts";

export function ContactsPage() {
  const { token } = useAuth();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const limit = 20;

  useEffect(() => {
    if (token == null) return;
    setLoading(true);
    listContacts(token, { search: search || undefined, limit, offset: page * limit })
      .then((res) => { setContacts(res.items); setTotal(res.total); })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Failed to load contacts"))
      .finally(() => setLoading(false));
  }, [token, search, page]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Contacts</h1>
          <span className="text-sm text-gray-500">{total} total</span>
        </div>

        <input
          type="text"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />

        {loading && <p className="text-gray-400">Loading…</p>}
        {error != null && <p className="text-red-400">{error}</p>}
        {!loading && contacts.length === 0 && <p className="text-gray-500">No contacts found.</p>}

        <div className="space-y-2">
          {contacts.map((c) => (
            <div key={c.id} className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium text-white">{c.firstName} {c.lastName}</p>
                  <p className="text-sm text-gray-400">{c.email}</p>
                  {c.title && <p className="text-xs text-gray-500 mt-0.5">{c.title}</p>}
                </div>
                {c.phone && <p className="text-sm text-gray-400 shrink-0">{c.phone}</p>}
              </div>
            </div>
          ))}
        </div>

        {total > limit && (
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="px-3 py-1 rounded bg-gray-800 text-sm text-gray-300 disabled:opacity-40">Previous</button>
            <span className="text-sm text-gray-500 self-center">{page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}</span>
            <button onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * limit >= total} className="px-3 py-1 rounded bg-gray-800 text-sm text-gray-300 disabled:opacity-40">Next</button>
          </div>
        )}
      </div>
    </Layout>
  );
}
